import React, {
	useCallback,
	useMemo,
	useRef,
	useState,
	useLayoutEffect,
} from "react";
import { UseTranslationResponse } from "react-i18next";
import { SortType } from "rsuite-table";
import { isString } from "lodash";

import { Language, TaxiService, Executor } from "../../../../../services";
import {
	useColumns,
	useVisibleColumns,
	useWidths,
} from "../../../../../hooks/useTableSettings";
import { TableSettings } from "../../../..";
import { useTableOptions, UseTableOptions } from "../../../../LightTable";
import CompactTable from "../../../../CompactTable";
import { StyledGrid, Colors } from "../../../../common";
import { ExecutorsWidget } from "../..";
import { EXECUTORS_TABLE_PAGE_SIZE } from "../../constants";

import columnBase, {
	ColumnId as LocalColumnId,
	defaultColumnIds,
} from "./columns";

const ModelTable: React.FC<ModelTable.Props> = ({
	loading,
	data,

	nameColuumnsGroup,

	editorTable,
	onChangeTable,
	tableFooter,
	tableRowClassName,
	onRowDoubleClick,
	onRowSelect,
}) => {
	const settingsRef = useRef<HTMLDivElement | null>(null);

	const STABILITY_HEIGHT_ROW = 32;

	const _nameColuumnsGroup = useMemo(
		() => nameColuumnsGroup,
		[nameColuumnsGroup],
	);

	const {
		tableRef,
		lang,
		onKeyDown,
		onKeyUp,
		onScroll,
		setDataLength,
		widthScroll,
		heightScroll,
		sort,
		onSort,
	} = useTableOptions({
		value: editorTable,
		setValue: onChangeTable,
		heightRow: STABILITY_HEIGHT_ROW,
		itemLimit: EXECUTORS_TABLE_PAGE_SIZE,
		saveSlot: _nameColuumnsGroup.reduxLocal,
		tableId: _nameColuumnsGroup.name,
	});

	useLayoutEffect(() => {
		setDataLength(data.length);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data.length]);

	const scrollPos = useMemo(
		() => ({ x: widthScroll, y: heightScroll }),
		[heightScroll, widthScroll],
	);

	const columns = useColumns(_nameColuumnsGroup.name);
	const { columnIds, setColumnIds } = useVisibleColumns(
		_nameColuumnsGroup.reduxLocal,
		_nameColuumnsGroup.name,
	);
	const { widths, setWidths } = useWidths(
		_nameColuumnsGroup.reduxLocal,
		_nameColuumnsGroup.name,
	);

	const modelTableColumns = useMemo<ModelTable.Column[]>(() => {
		const _columnIds = columnIds as LocalColumnId[];

		return columns
			.map((column) => {
				const id = column.id as LocalColumnId;

				const retval = {
					id,
					width: widths[id],
					visible: _columnIds?.includes(id) ?? false,
				};
				return retval;
			})
			.sort((leftColumn, rightColumn) => {
				const leftIndex = _columnIds.indexOf(leftColumn.id);
				const rightIndex = _columnIds.indexOf(rightColumn.id);
				return leftIndex - rightIndex;
			});
	}, [columns, widths, columnIds]);

	const rowHeight = useCallback((rowData) => {
		const taxiServiceDescription = rowData?.description;
		const defaultHeight = STABILITY_HEIGHT_ROW;

		if (taxiServiceDescription && isString(taxiServiceDescription)) {
			const fontSize = 13;
			const fontBetween = 3;
			const lineHeight = fontSize + fontBetween;
			const padding = 28;

			const lines = taxiServiceDescription.split("\n").length;

			const calculatedHeight = lines * lineHeight;

			return Math.max(defaultHeight, calculatedHeight + padding);
		}

		return defaultHeight;
	}, []);

	const modelTableOnChangeColumns = useCallback(
		(columns: ModelTable.Column[]) => {
			setColumnIds(
				columns
					.filter((column) => column.visible)
					.map((column) => column.id),
			);
			setWidths(
				columns.reduce((accumulator, column) => {
					accumulator[column.id] = column.width;
					return accumulator;
				}, {} as Record<LocalColumnId, number>),
			);
		},
		[setColumnIds, setWidths],
	);

	const tableContainerRef = useRef<HTMLDivElement | null>(null);
	const [tableViewportWidth, setTableViewportWidth] = useState(0);

	useLayoutEffect(() => {
		if (!tableContainerRef.current) return () => {};

		const updateSize = () => {
			const width = tableContainerRef.current?.clientWidth ?? 0;
			setTableViewportWidth(width);
		};

		updateSize();

		const resizeObserver = new ResizeObserver(updateSize);
		resizeObserver.observe(tableContainerRef.current);

		return () => resizeObserver.disconnect();
	}, []);

	const renderedColumns = useMemo(() => {
		const visibleColumns = modelTableColumns.filter(
			(column) => column.visible,
		);
		const totalWidth = visibleColumns.reduce(
			(sum, column) => sum + column.width,
			0,
		);

		return visibleColumns.map((column, index) => {
			const isLastColumn = index === visibleColumns.length - 1;
			const isMustWithColumnThanViewport =
				totalWidth < tableViewportWidth;
			const widthWithAvtoFix =
				tableViewportWidth > 0 &&
				isLastColumn &&
				isMustWithColumnThanViewport
					? tableViewportWidth + column.width - totalWidth
					: column.width;

			const node = columnBase[column.id]?.render({
				width: widthWithAvtoFix,
				language: lang,

				onResize: (width, columnId) => {
					const newColumns = modelTableColumns.map((columns) =>
						columns.id === columnId
							? { ...columns, width: width ?? 0 }
							: columns,
					);

					modelTableOnChangeColumns(newColumns);
				},
			});

			return node;
		});
	}, [
		modelTableColumns,
		tableViewportWidth,
		lang,
		modelTableOnChangeColumns,
	]);

	return (
		<StyledGrid areas="content footer" rows="1fr auto" w="100%" h="100%">
			<div
				ref={tableContainerRef}
				tabIndex={-1}
				onKeyDown={onKeyDown}
				onKeyUp={onKeyUp}
				style={{
					height: "100%",
					minHeight: 0,
					overflow: "hidden",
				}}
			>
				<CompactTable
					ref={tableRef}
					fillHeight
					virtualized
					headerHeight={STABILITY_HEIGHT_ROW}
					rowHeight={rowHeight}
					loading={loading}
					data={data ?? []}
					rowClassName={tableRowClassName}
					shouldUpdateScroll={(name) =>
						name === "widthChanged" ? scrollPos : {}
					}
					onRowClick={onRowSelect}
					onRowDoubleClick={onRowDoubleClick}
					onScroll={onScroll}
					sortColumn={sort?.dataKey}
					sortType={sort?.sortType}
					onSortColumn={onSort}
					style={{
						background: Colors.BACKGROUND_BLUE_1,
					}}
				>
					{renderedColumns}
				</CompactTable>
			</div>

			{tableFooter}

			<TableSettings
				ref={settingsRef}
				value={columnIds}
				defaultValue={defaultColumnIds}
				columns={columns}
				onChange={setColumnIds}
			/>
		</StyledGrid>
	);
};

declare namespace ModelTable {
	interface Column {
		id: LocalColumnId;
		width: number;
		visible: boolean;
	}

	interface Sort {
		column?: string;
		type?: SortType;
	}

	interface Props {
		onRowDoubleClick: (rowData) => void;
		onRowSelect: (rowData) => void;
		tableRowClassName: (rowData) => string;

		loading: boolean;
		data: Executor.Model[];

		nameColuumnsGroup: ExecutorsWidget.TabMain;
		//

		editorTable: UseTableOptions["editor"];
		onChangeTable: UseTableOptions["onChange"];
		tableFooter: React.ReactNode;
	}

	interface ColumnContext {
		language: Language;
		t: UseTranslationResponse<"translation", undefined>[0];
		taxiServiceById: Record<number, TaxiService.Model>;
		width: number;
		onResize: (width?: number, columnId?: string) => void;
	}
}

export default ModelTable;
